import type { ActionItem } from './types'

// Navigate target for an ActionItem row. Evidence rows are CES tests —
// they go to the test detail page so the user lands on the same UI as
// /tests/$testId. Procedure-run rows (RFC-206 §7.1) waiting on a single
// human step open the task detail page, which renders that step's completer
// (the same surface as the inbox item) so the user can fill it in; failed or
// multi-step runs open the run detail, where the run is the source of truth.
// Everything else uses the generic task detail page.
// Both Tasks page and chat-welcome Tasks block route through this.
export type ActionItemRoute =
  | {
      to: '/workspaces/$workspaceId/reviews/access/period/$periodKey'
      params: { workspaceId: string; periodKey: string }
    }
  | {
      to: '/workspaces/$workspaceId/reviews/access/$cycleId'
      params: { workspaceId: string; cycleId: string }
    }
  | {
      to: '/workspaces/$workspaceId/tests/$testId'
      params: { workspaceId: string; testId: string }
      search: { from: 'tasks'; highlightFinding?: string }
    }
  | {
      to: '/workspaces/$workspaceId/runs/$executionId'
      params: { workspaceId: string; executionId: string }
    }
  | {
      to: '/workspaces/$workspaceId/tasks/$taskId'
      params: { workspaceId: string; taskId: string }
      search: { category: string }
    }

export function getActionItemRoute(
  item: Pick<ActionItem, 'category' | 'id' | 'meta'>,
  workspaceId: string,
): ActionItemRoute {
  if (item.category === 'evidence') {
    if (item.meta?.accessReviewPeriodId && typeof item.meta.periodKey === 'string') {
      return { to: '/workspaces/$workspaceId/reviews/access/period/$periodKey', params: { workspaceId, periodKey: item.meta.periodKey } }
    }
    const accessReviewId = item.meta?.accessReviewId
    if (typeof accessReviewId === 'string' && accessReviewId) {
      return {
        to: '/workspaces/$workspaceId/reviews/access/$cycleId',
        params: { workspaceId, cycleId: accessReviewId },
      }
    }
    return {
      to: '/workspaces/$workspaceId/tests/$testId',
      params: { workspaceId, testId: item.id },
      search: { from: 'tasks' },
    }
  }
  // #1418 — a CES-linked finding's actionable home is its test detail
  // (TestOpenFindingsSection has dismiss/remediate right there); highlight
  // the finding so the user lands on it. Standalone findings keep the
  // generic task detail page, which carries the remediation actions.
  if (item.category === 'remediation') {
    const cesId = item.meta?.cesId
    if (typeof cesId === 'string' && cesId) {
      return {
        to: '/workspaces/$workspaceId/tests/$testId',
        params: { workspaceId, testId: cesId },
        search: { from: 'tasks', highlightFinding: item.id },
      }
    }
  }
  if (item.category === 'procedure_run') {
    const stepRunId = item.meta?.stepRunId
    if (typeof stepRunId === 'string' && stepRunId) {
      return {
        to: '/workspaces/$workspaceId/tasks/$taskId',
        params: { workspaceId, taskId: item.id },
        search: { category: item.category },
      }
    }
    return {
      to: '/workspaces/$workspaceId/runs/$executionId',
      params: { workspaceId, executionId: item.id },
    }
  }
  return {
    to: '/workspaces/$workspaceId/tasks/$taskId',
    params: { workspaceId, taskId: item.id },
    search: { category: item.category },
  }
}
